import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileSpreadsheet, Search, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";

const SOURCES = [
  { key: "scanx", label: "ScanX" },
  { key: "configurator", label: "Configurator" },
  { key: "obloane", label: "Obloane" },
];

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Coloane IDENTICE cu Istoric oferte (oferte.amel-online.ro), aceeasi ordine.
// Trebuie sa corespunda exact cu COLUMNS din backend/routes/configurator_routes.py.
const COLUMNS = [
  ["sursa", "Sursa"],
  ["data", "Data"],
  ["client", "Client"],
  ["localitate", "Loc."],
  ["utilizator", "Utilizator"],
  ["email", "Adresa mail"],
  ["telefon", "Nr telefon"],
  ["nr_oferta", "Nr oferta"],
  ["poz", "Poz"],
  ["serie", "Serie"],
  ["esenta", "Esenta"],
  ["lamela", "Lamela"],
  ["culoare", "Culoare"],
  ["buc", "Buc"],
  ["total_fabrica", "Pret fabrica"],
  ["total", "Pret cu adaos"],
  ["transport", "Transport"],
  ["montaj", "Montaj"],
  ["adaos", "Adaos"],
];

const DEALER_COLUMNS = [
  ["utilizator", "Utilizator"],
  ["email", "Adresa mail"],
  ["telefon", "Nr telefon"],
  ["nr_oferte", "Nr oferte"],
  ["poz", "Poz"],
  ["buc", "Buc"],
  ["total_fabrica", "Pret fabrica"],
  ["total", "Pret cu adaos"],
  ["transport", "Transport"],
  ["montaj", "Montaj"],
  ["adaos", "Adaos"],
];

const VIEWS = {
  detaliat: { label: "Oferte", endpoint: "/api/configurator/tabel-oferte", file: "oferte_configurator", columns: COLUMNS, unit: "oferte" },
  dealer: { label: "Cumulat dealer", endpoint: "/api/configurator/cumulat-dealer", file: "cumulat_dealer", columns: DEALER_COLUMNS, unit: "utilizatori" },
  dealerLitera: { label: "Cumulat editate", endpoint: "/api/configurator/cumulat-dealer-litera", file: "cumulat_editate", columns: DEALER_COLUMNS, unit: "utilizatori" },
};

export default function Configurator() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState({ scanx: true, configurator: true, obloane: true });
  const [dateStart, setDateStart] = useState(firstOfMonth());
  const [dateEnd, setDateEnd] = useState(today());
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("detaliat");
  const [total, setTotal] = useState(null);
  const cfg = VIEWS[view];
  const cols = cfg.columns;

  const switchView = (v) => {
    setView(v);
    setRows(null);
    setMeta(null);
    setTotal(null);
  };

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  const buildParams = useCallback(() => {
    const chosen = SOURCES.filter((s) => selected[s.key]).map((s) => s.key);
    const allSelected = chosen.length === SOURCES.length || chosen.length === 0;
    const params = {};
    params.sursa = allSelected ? "toate" : chosen.join(",");
    if (dateStart) params.date_start = dateStart;
    if (dateEnd) params.date_end = dateEnd;
    return params;
  }, [selected, dateStart, dateEnd]);

  const load = useCallback(async () => {
    const chosen = SOURCES.filter((s) => selected[s.key]);
    if (chosen.length === 0) {
      toast.error("Selecteaza cel putin o sursa.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(cfg.endpoint, { params: buildParams() });
      setRows(data.rows);
      setTotal(data.total || null);
      setMeta({ count: data.count, truncated: data.truncated, limit: data.limit });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nu am putut incarca tabelul");
    } finally {
      setLoading(false);
    }
  }, [buildParams, selected, cfg]);

  const onExport = useCallback(async () => {
    const chosen = SOURCES.filter((s) => selected[s.key]);
    if (chosen.length === 0) {
      toast.error("Selecteaza cel putin o sursa.");
      return;
    }
    setExporting(true);
    try {
      const resp = await api.get(`${cfg.endpoint}/export`, {
        params: buildParams(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `${cfg.file}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel descarcat");
    } catch (err) {
      toast.error("Export esuat");
    } finally {
      setExporting(false);
    }
  }, [buildParams, selected, cfg]);

  return (
    <Layout title="Configuratorul">
      <section className="mb-6">
        <button
          type="button"
          data-testid="configurator-back-btn"
          onClick={() => navigate("/aplicatii")}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft size={15} /> Inapoi la Aplicatii
        </button>
        <p className="text-xs uppercase tracking-widest text-slate-400">Aplicatia Configuratorul</p>
        <h2 data-testid="configurator-title" className="text-2xl font-semibold">
          Oferte Configurator
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          Oferte din oferte.amel-online.ro, citite read-only. Grupate pe numar oferta.
          Filtreaza dupa sursa (ScanX, Configurator, Obloane) si dupa data ofertei.
          Export in Excel.
        </p>
      </section>

      <div data-testid="configurator-view-tabs" className="mb-4 inline-flex rounded-md bg-slate-100 p-1 text-sm">
        {Object.entries(VIEWS).map(([v, c]) => (
          <button key={v} type="button" data-testid={`configurator-view-${v}`} onClick={() => switchView(v)}
            className={`rounded px-3 py-1 ${view === v ? "bg-white font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{c.label}</button>
        ))}
      </div>

      <section
        data-testid="configurator-filters"
        className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
      >
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <div className="mb-2 text-xs uppercase tracking-widest text-slate-400">Sursa</div>
            <div className="flex flex-wrap gap-2">
              {SOURCES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`source-toggle-${s.key}`}
                  onClick={() => toggle(s.key)}
                  className={`rounded border px-3 py-1.5 text-sm transition ${
                    selected[s.key]
                      ? "border-accent-500 bg-accent-500/10 text-accent-500"
                      : "border-slate-200 bg-slate-100 text-slate-500 hover:border-slate-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400" htmlFor="date-start">
              De la (data oferta)
            </label>
            <input
              id="date-start"
              data-testid="filter-date-start"
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="rounded border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm outline-none focus:border-accent-500"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-slate-400" htmlFor="date-end">
              Pana la
            </label>
            <input
              id="date-end"
              data-testid="filter-date-end"
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className="rounded border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm outline-none focus:border-accent-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="generate-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded bg-accent-500 px-4 py-2 text-sm font-semibold text-ink-950 hover:bg-accent-600 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />} Genereaza
            </button>
            <button
              type="button"
              data-testid="export-excel-btn"
              onClick={onExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60"
            >
              {exporting ? <Loader2 className="animate-spin" size={15} /> : <FileSpreadsheet size={15} />} Export Excel
            </button>
          </div>
        </div>
      </section>

      {meta ? (
        <div data-testid="result-meta" className="mb-3 flex items-center gap-3 text-sm text-slate-500">
          <Download size={14} className="text-accent-500" />
          <span data-testid="result-count">{meta.count} {cfg.unit}</span>
          {view === "dealer" ? <span>(doar oferte de baza, fara litera la final)</span> : null}
          {view === "dealerLitera" ? <span>(doar oferte cu litera la final, ex. Amel-0960a)</span> : null}
          {meta.truncated ? (
            <span className="text-amber-700">
              (afisate primele {meta.limit}; restrange perioada pentru toate)
            </span>
          ) : null}
        </div>
      ) : null}

      <section className="overflow-x-auto rounded-lg border border-slate-200">
        <table data-testid="tabel-oferte-configurator" className="min-w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              {cols.map(([key, label]) => (
                <th key={key} className="px-3 py-2 font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows == null ? (
              <tr>
                <td colSpan={cols.length} className="px-3 py-8 text-center text-slate-400">
                  Seteaza filtrele si apasa Genereaza.
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={cols.length} className="px-3 py-8 text-center text-slate-400">
                  Nicio oferta pentru filtrele alese.
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={i}
                  data-testid={`tabel-row-${i}`}
                  className="border-t border-slate-100 odd:bg-white even:bg-slate-50"
                >
                  {cols.map(([key]) => (
                    <td key={key} className="px-3 py-2 align-top">
                      <span className="text-slate-700">{row[key]}</span>
                    </td>
                  ))}
                </tr>
              ))
            )}
            {rows && rows.length > 0 && total ? (
              <tr data-testid="tabel-row-total" className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                {cols.map(([key]) => (
                  <td key={key} className="px-3 py-2">{total[key]}</td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
